import React, { useMemo, useState } from 'react';
import styled from '@2pulse/responsive';
import { normalize } from '@app/helpers';
import { 
  ICatalogSnapshot, 
  ICatalogSnapshotToShop, 
  IProduct, 
  IPack,
  convertToEuros 
} from '@app/common';
import { SvgEdit } from '@app/components/icons/SvgEdit';
import { theme } from '@app/theme';
import { TextInput } from '@app/components';

interface CatalogShopListProps {
  catalogSnapshot: ICatalogSnapshot | undefined;
  catalogSnapshotToShop: ICatalogSnapshotToShop | undefined;
  onEditProduct: (product: IProduct) => void;
  onEditPack: (pack: IPack) => void;
  isLoading?: boolean;
}

export const CatalogShopList = ({ 
  catalogSnapshot, 
  catalogSnapshotToShop, 
  onEditProduct, 
  onEditPack,
  isLoading 
}: CatalogShopListProps) => {
  const [search, setSearch] = useState('');

  const products = useMemo(() => {
    if (!catalogSnapshot) return [];
    // Note: In a real implementation, you'd need access to catalogSnapshot.data.products
    // For now, we'll work with the catalogToProducts from the catalog
    return [];
  }, [catalogSnapshot]);

  const packs = useMemo(() => {
    if (!catalogSnapshot) return [];
    return [];
  }, [catalogSnapshot]);

  const getProductOverride = (productId: string) => {
    return catalogSnapshotToShop?.data?.productsOverrides?.find(
      override => override.productId === productId
    );
  };

  const getPackOverride = (packId: string) => {
    return catalogSnapshotToShop?.data?.packsOverrides?.find(
      override => override.packId === packId
    );
  };

  const hasProductOverrides = (productId: string) => {
    const override = getProductOverride(productId);
    if (!override) return false;
    
    return override.disabled !== null || 
      override.productVariations.some(v => 
        v.price !== null || 
        v.deliveryPrice !== null || 
        v.takeAwayPrice !== null || 
        v.onSpotPrice !== null ||
        v.disabled !== null
      );
  };

  const hasPackOverrides = (packId: string) => {
    const override = getPackOverride(packId);
    if (!override) return false;
    
    return override.disabled !== null ||
      override.price !== null ||
      override.deliveryPrice !== null ||
      override.takeAwayPrice !== null ||
      override.onSpotPrice !== null;
  };

  if (isLoading) {
    return (
      <Container>
        <LoadingText>Chargement...</LoadingText>
      </Container>
    );
  }

  if (!catalogSnapshot) {
    return (
      <Container>
        <EmptyText>Sélectionnez un catalogue pour voir les produits et menus</EmptyText>
      </Container>
    );
  }

  return (
    <Container>
      <SearchContainer>
        <SearchInput
          placeholder="Rechercher..."
          value={search}
          onChangeText={setSearch}
        />
      </SearchContainer>

      <SectionTitle>Menus</SectionTitle>
      {packs.length === 0 ? (
        <EmptyText>Aucun menu dans ce catalogue</EmptyText>
      ) : (
        packs.map((pack: IPack) => (
          <ItemRow key={pack._id} onPress={() => onEditPack(pack)}>
            <ItemInfo>
              <ItemName>{pack.name}</ItemName>
              {hasPackOverrides(pack._id) && (
                <OverrideBadge>
                  <OverrideBadgeText>Modifié</OverrideBadgeText>
                </OverrideBadge>
              )}
            </ItemInfo>
            <ItemPrice>
              {convertToEuros(pack.price).toFixed(2)} €
            </ItemPrice>
            <EditButton>
              <SvgEdit color={theme.colors.celadonblue} />
            </EditButton>
          </ItemRow>
        ))
      )}

      <SectionTitle>Produits</SectionTitle>
      {products.length === 0 ? (
        <EmptyText>Aucun produit dans ce catalogue</EmptyText>
      ) : (
        products.map((product: IProduct) => (
          <ItemRow key={product._id} onPress={() => onEditProduct(product)}>
            <ItemInfo>
              <ItemName>{product.name}</ItemName>
              {hasProductOverrides(product._id) && (
                <OverrideBadge>
                  <OverrideBadgeText>Modifié</OverrideBadgeText>
                </OverrideBadge>
              )}
            </ItemInfo>
            <ItemPrice>
              {product.productVariations?.length || 0} déclinaison(s)
            </ItemPrice>
            <EditButton>
              <SvgEdit color={theme.colors.celadonblue} />
            </EditButton>
          </ItemRow>
        ))
      )}
    </Container>
  );
};

const Container = styled.ScrollView({
  flex: 1,
  padding: normalize(20),
});

const SearchContainer = styled.View({
  marginBottom: normalize(20),
});

const SearchInput = styled.TextInput(({ theme }) => ({
  backgroundColor: theme.colors.babypowder,
  borderRadius: normalize(8),
  padding: normalize(12),
  fontFamily: 'Work Sans',
  fontSize: normalize(14),
}));

const SectionTitle = styled.Text(({ theme }) => ({
  fontFamily: 'Work Sans',
  fontSize: normalize(18),
  fontWeight: '700',
  color: theme.colors.prussianblue,
  marginTop: normalize(20),
  marginBottom: normalize(12),
}));

const ItemRow = styled.TouchableOpacity(({ theme }) => ({
  flexDirection: 'row',
  alignItems: 'center',
  backgroundColor: theme.colors.babypowder,
  borderRadius: normalize(8),
  padding: normalize(16),
  marginBottom: normalize(8),
}));

const ItemInfo = styled.View({
  flex: 1,
  flexDirection: 'row',
  alignItems: 'center',
  gap: normalize(8),
});

const ItemName = styled.Text(({ theme }) => ({
  fontFamily: 'Work Sans',
  fontSize: normalize(14),
  fontWeight: '500',
  color: theme.colors.prussianblue,
}));

const OverrideBadge = styled.View(({ theme }) => ({
  backgroundColor: theme.colors.celadonblue,
  borderRadius: normalize(4),
  paddingHorizontal: normalize(8),
  paddingVertical: normalize(2),
}));

const OverrideBadgeText = styled.Text(({ theme }) => ({
  fontFamily: 'Work Sans',
  fontSize: normalize(10),
  fontWeight: '600',
  color: theme.colors.babypowder,
}));

const ItemPrice = styled.Text(({ theme }) => ({
  fontFamily: 'Work Sans',
  fontSize: normalize(14),
  color: theme.colors.satanGray,
  marginRight: normalize(12),
}));

const EditButton = styled.View({
  padding: normalize(8),
});

const EmptyText = styled.Text(({ theme }) => ({
  fontFamily: 'Work Sans',
  fontSize: normalize(14),
  color: theme.colors.satanGray,
  textAlign: 'center',
  padding: normalize(20),
}));

const LoadingText = styled.Text(({ theme }) => ({
  fontFamily: 'Work Sans',
  fontSize: normalize(14),
  color: theme.colors.satanGray,
  textAlign: 'center',
  padding: normalize(40),
}));
